/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import classNames from 'classnames';
import { Icon, IconColor, IconName } from 'app/common/components/controls';
import { Noun, Verb, trackUe } from '@trello/analytics';
import HeaderLink from 'app/gamma/src/components/header/header-link';
import { SearchResultsPopoverId } from 'app/gamma/src/components/popovers/popover-ids';
import { ScreenBreakpoints } from 'app/gamma/src/components/responsive';
import {
  getKey,
  Key,
  registerShortcut,
  Scope,
  unregisterShortcut,
} from '@trello/keybindings';
import RouterLink from 'app/common/components/singlespa/router-link';
import {
  clearSearchQuery,
  clearSearchSuggestions,
  closePopover,
  closePopovers,
  getSearchSuggestions,
  hideAddSavedSearchForm,
  hideSavedSearchPromo,
  performSearch,
  setSearchQuery,
  setSelectedSuggestion,
  showPopover,
  State,
} from 'app/gamma/src/modules';
import React, { lazy, Suspense } from 'react';
import Media from 'react-media';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  areAnyPopoversOrOverlaysCurrentlyOpen,
  isPopoverShown,
} from 'app/gamma/src/selectors/popovers';
import { getBoardsByIds } from 'app/gamma/src/selectors/boards';
import { getMembersByIds } from 'app/gamma/src/selectors/members';
import {
  getCurrentSearchQuery,
  getSearchSuggestions as selectSearchSuggestions,
  getSelectedSuggestion,
  getLastSearchQuery,
} from 'app/gamma/src/selectors/search';
import dynamicDebounce from 'app/gamma/src/util/dynamic-debounce';
import randomToken from 'app/gamma/src/util/random-token';
import styles from './form.less';
import {
  BoardModel,
  MemberModel,
  SearchSuggestionType as SuggestionType,
} from 'app/gamma/src/types/models';

const SearchResultsPopoverPromise = import(/* webpackChunkName: "search-results-popover" */ 'app/gamma/src/components/popovers/search-results');
const SearchResultsPopover = lazy(() => SearchResultsPopoverPromise);

const inputId = randomToken(32);

interface StateProps {
  isActive: boolean;
  isAnyPopoverOpen: boolean;
  currentQuery: string;
  lastQuery: string;
  allSuggestions: (SuggestionType | BoardModel | MemberModel)[];
  keywords: SuggestionType[];
  boards: BoardModel[];
  members: MemberModel[];
  selectedSuggestion: string;
  brandingLogo: string | undefined;
}

interface DispatchProps {
  clearSearchInput: () => void;
  onInputBlur: () => void;
  onInputClick: () => void;
  onResetSearch: () => void;
  onSearch: (query: string) => void;
  onSuggest: (query: string, caretPos: number) => void;
  onShortcutFocus: () => void;
  onSetSearchQuery: (query: string) => void;
  onSearchSuggestion: (query: string) => void;
  setSelectedSuggestion: (selected: string | null) => void;
}

interface AllProps extends StateProps, DispatchProps {}

class SearchForm extends React.Component<AllProps> {
  inputRef = React.createRef<HTMLInputElement>();

  private getSearchDelay = () => {
    const { currentQuery } = this.props;
    if (currentQuery.length <= 1) {
      return 1800;
    } else if (currentQuery.length <= 3) {
      return 1200;
    }

    return 600;
  };

  private debouncedSearch = dynamicDebounce(
    this.props.onSearch,
    this.getSearchDelay,
  );

  private debouncedSuggest = dynamicDebounce(this.props.onSuggest, 100);

  componentDidMount() {
    const { currentQuery, onSetSearchQuery } = this.props;

    if (currentQuery !== '') {
      onSetSearchQuery('');
    }
    document.addEventListener('click', this.stopClickPropagation, true);
    registerShortcut(this.onShortcut, {
      scope: Scope.Global,
      key: Key.ForwardSlash,
    });
  }

  componentDidUpdate(prevProps: AllProps) {
    // This is for when `this.props.onInputBlur` gets called without a MouseEvent
    // (namely when the user presses `Esc`). Without this, the popover closes,
    // but the input remains in the active state
    if (this.inputRef.current && !this.props.isActive && prevProps.isActive) {
      this.inputRef.current.blur();
    }
    // Also needed for focus without mouse event
    if (
      this.inputRef.current &&
      this.props.isActive &&
      (!prevProps.isActive ||
        this.props.currentQuery === '' ||
        this.props.currentQuery !== prevProps.currentQuery)
    ) {
      this.inputRef.current.focus();
    }
  }

  componentWillUnmount() {
    this.debouncedSearch.clear();
    document.removeEventListener('click', this.stopClickPropagation, true);
    unregisterShortcut(this.onShortcut);
  }

  /**
   * Because the search results popover is shown/hidden in response to the
   * focus and blur events and because those events are dispatched before
   * click events, we need to call preventDefault() to keep the popover from
   * closing itself when the input is clicked.
   */
  stopClickPropagation = (e: MouseEvent) => {
    const inputNode = this.inputRef.current;
    if (e.target === inputNode) {
      e.preventDefault();
    }
  };

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.currentTarget.value;
    const { current } = this.inputRef;
    this.props.onSetSearchQuery(query);
    if (query.length) {
      this.debouncedSearch(query);
      this.debouncedSuggest(
        query,
        current && current.selectionStart ? current.selectionStart : 0,
      );
    }
  };

  onBlur = () => {
    this.props.clearSearchInput();

    // prevents calling this twice when pressing escape
    if (this.props.isActive) {
      this.props.onInputBlur();
    }
  };

  onShortcut = () => {
    const { isAnyPopoverOpen, onShortcutFocus } = this.props;
    if (!isAnyPopoverOpen) {
      onShortcutFocus();

      trackUe({
        category: Noun.HEADER,
        verb: Verb.OPENS,
        directObj: Noun.SEARCH_INPUT,
        method: 'by using search shortcut',
      });
    }
  };

  onMouseDown = () => {
    const { isActive, onInputClick } = this.props;

    if (!isActive) {
      onInputClick();
    }
  };

  getSelectedIndex = (): number => {
    const { boards, members, keywords, selectedSuggestion } = this.props;
    let index = -1;
    if (keywords.length) {
      index = keywords.findIndex((keyword) => keyword === selectedSuggestion);
    }
    if (index === -1 && (boards.length || members.length)) {
      index = [...boards, ...members].findIndex(
        (suggestion) => suggestion.id === selectedSuggestion,
      );
    }

    return index;
  };

  getInitalQuery = () => {
    const { currentQuery } = this.props;

    return currentQuery.substring(0, currentQuery.lastIndexOf(' ') + 1);
  };

  onBoardSuggestion = (board: BoardModel) => {
    this.props.onSearchSuggestion(
      `${this.getInitalQuery()}board:"${board.name}" `,
    );
  };

  onMemberSuggestion = (member: MemberModel) => {
    this.props.onSearchSuggestion(
      `${this.getInitalQuery()}@${member.username} `,
    );
  };

  onKeywordSuggestion = (keyword: string) => {
    this.props.onSearchSuggestion(`${this.getInitalQuery()}${keyword} `);
  };

  onUpArrow = (e: React.KeyboardEvent<HTMLElement>) => {
    const { allSuggestions } = this.props;
    if (!allSuggestions.length) {
      return;
    }
    e.preventDefault();
    const index = this.getSelectedIndex();
    const next: number = index > 0 ? index - 1 : allSuggestions.length - 1;
    const nextSuggestion = allSuggestions[next];
    this.props.setSelectedSuggestion(
      // @ts-ignore
      nextSuggestion.id ? nextSuggestion.id : nextSuggestion,
    );
  };

  onDownArrow = (e: React.KeyboardEvent<HTMLElement>) => {
    const { allSuggestions } = this.props;
    if (!allSuggestions.length) {
      return;
    }
    e.preventDefault();
    const index = this.getSelectedIndex();
    const length: number = allSuggestions.length - 1;
    const next: number = index === length ? 0 : index + 1;
    const nextSuggestion = allSuggestions[next];
    this.props.setSelectedSuggestion(
      // @ts-ignore
      nextSuggestion.id ? nextSuggestion.id : nextSuggestion,
    );
  };

  onEnter = (e: React.KeyboardEvent<HTMLElement>) => {
    const { allSuggestions, keywords, members, boards } = this.props;
    if (!allSuggestions.length) {
      return;
    }
    e.preventDefault();
    const index = this.getSelectedIndex();
    if (index < keywords.length) {
      this.onKeywordSuggestion(keywords[index]);
    } else if (
      index >= keywords.length &&
      index < keywords.length + boards.length
    ) {
      this.onBoardSuggestion(boards[index - keywords.length]);
    } else {
      this.onMemberSuggestion(
        members[index - (keywords.length + boards.length)],
      );
    }
  };

  onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    switch (getKey(e)) {
      case Key.Escape:
        this.onBlur();
        e.preventDefault();
        e.stopPropagation();
        break;

      case Key.ArrowUp:
        this.onUpArrow(e);
        break;

      case Key.ArrowDown:
        this.onDownArrow(e);
        break;

      case Key.Enter:
      case Key.Tab:
        this.onEnter(e);
        break;

      default:
    }
  };

  render() {
    const {
      currentQuery,
      isActive = false,
      lastQuery,
      onShortcutFocus,
      brandingLogo,
    } = this.props;

    return (
      <>
        {isActive && (
          <Suspense fallback={null}>
            <SearchResultsPopover
              inputRef={this.inputRef}
              onClose={this.onBlur}
              target={() => this.inputRef.current}
            />
          </Suspense>
        )}
        <Media query={ScreenBreakpoints.Small}>
          {(matches: boolean) =>
            matches ? (
              <HeaderLink href="/search" buttonStyle="button">
                <Icon name={IconName.Search} color={IconColor.Light} medium />
              </HeaderLink>
            ) : (
              <div
                className={classNames(
                  styles.search,
                  isActive && styles.active,
                  brandingLogo && styles.branded,
                )}
              >
                <input
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className={styles.searchInput}
                  data-test-id={HeaderTestIds.SearchInput}
                  id={inputId}
                  onMouseDown={this.onMouseDown}
                  onFocus={onShortcutFocus}
                  onKeyDown={this.onKeyDown}
                  onChange={this.onChange}
                  ref={this.inputRef}
                  type="search"
                  value={currentQuery}
                />
                <span className={styles.buttons}>
                  {isActive ? (
                    <>
                      <RouterLink
                        href={`/search?q=${encodeURIComponent(lastQuery)}`}
                      >
                        <Icon
                          className={classNames([
                            styles.button,
                            styles.externalIconButton,
                          ])}
                          medium
                          name={IconName.ExternalLink}
                        />
                      </RouterLink>
                      <Icon
                        className={styles.button}
                        medium
                        name={IconName.Close}
                        testId={HeaderTestIds.SearchClose}
                      />
                    </>
                  ) : (
                    <label htmlFor={inputId}>
                      <Icon
                        className={styles.button}
                        medium
                        name={IconName.Search}
                      />
                    </label>
                  )}
                </span>
              </div>
            )
          }
        </Media>
      </>
    );
  }
}

const mapStateToProps = (state: State): StateProps => {
  const { keywords, idMembers, idBoards } = selectSearchSuggestions(state);
  const boards: BoardModel[] = getBoardsByIds(state, idBoards);
  const members: MemberModel[] = getMembersByIds(state, idMembers);
  const selectedSuggestion = getSelectedSuggestion(state);
  const allSuggestions = [...keywords, ...boards, ...members];
  const currentQuery = getCurrentSearchQuery(state);
  const lastQuery = getLastSearchQuery(state);

  return {
    isActive: isPopoverShown(state, SearchResultsPopoverId),
    isAnyPopoverOpen: areAnyPopoversOrOverlaysCurrentlyOpen(state),
    brandingLogo: state.ui.header.brandingLogo,
    allSuggestions,
    currentQuery,
    lastQuery,
    keywords,
    boards,
    members,
    selectedSuggestion,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<State>): DispatchProps => ({
  clearSearchInput() {
    dispatch(clearSearchQuery());
  },
  onInputClick() {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.SEARCH_INPUT,
      method: 'by clicking on search input',
    });
  },

  onSearch(query: string) {
    dispatch(performSearch({}));
  },

  onSuggest(query: string, caretPos: number) {
    dispatch(getSearchSuggestions(caretPos));
  },

  onSetSearchQuery(query: string) {
    dispatch(setSearchQuery({ query }));
  },

  onInputBlur() {
    dispatch(closePopover(SearchResultsPopoverId));
    dispatch(hideAddSavedSearchForm());
    dispatch(hideSavedSearchPromo());

    trackUe({
      category: Noun.HEADER,
      verb: Verb.CLOSES,
      directObj: Noun.SEARCH_INPUT,
    });
  },

  onShortcutFocus() {
    dispatch(closePopovers());
    dispatch(showPopover({ name: SearchResultsPopoverId }));
  },
  onResetSearch() {
    dispatch(clearSearchQuery());
  },
  onSearchSuggestion(query: string) {
    dispatch(clearSearchSuggestions());
    dispatch(setSearchQuery({ query }));
    dispatch(performSearch({}));
  },
  setSelectedSuggestion(selected: string | null) {
    dispatch(setSelectedSuggestion(selected));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SearchForm);
